import React from 'react';
import {
  CREATE_LEAD,
  FINALLY_NOT_ACTIVE_STATUSES,
  LEAD_STATUS,
  NOT_ACTIVE_STATUSES,
  READ_FULL_LEADS_LIST,
  UPDATE_LEAD_FROM_LIST_PAGE,
} from 'domain/sales/lead/shared/leadService';
import {
  getUrlStorageParams,
  QuickFilter,
  DateTimeRangeFilter,
  useFilterContext,
  FiltersLayout,
  ListHeader,
  DataGridLayout,
  ListPageLayout,
} from 'framework/list';
import { CreateButton } from 'framework/ui/ui-builder';
import LeadSearchInput from 'domain/sales/lead/shared/field/LeadSearchInput';
import EditLeadButton from 'domain/sales/lead/shared/button/EditLeadButton';
import {
  BadgeField,
  SearchLinkField,
  SearchResultField,
  ExpandingTooltipField,
  EmailSearchField,
  CountryFlagField,
} from 'framework/ui/field';
import LeadWillAutoDisqualifiedSoonBadge from 'domain/sales/lead/shared/field/LeadWillAutoDisqualifiedSoonBadge';
import LastTouchCrmField from 'domain/sales/shared/field/LastTouchCrmField';
import LeadStatusField from 'domain/sales/lead/shared/field/LeadStatusField';
import { EmployeeLinkField } from 'domain/shared/employee';
import LeadGradeField from 'domain/sales/lead/shared/field/LeadGradeField';
import LeadAgeField from 'domain/sales/lead/shared/field/LeadAgeField';
import ReadByOwnerCheckboxField from 'domain/sales/shared/field/ReadByOwnerCheckboxField';
import { useAuthorization } from 'framework/authorization';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { useLeadUrl } from 'domain/shared/lead';
import { NULL_AS_STRING, useDidUpdateEffect, useAsyncValue } from 'framework/utils';
import LeadStatusFilter from './filter/LeadStatusFilter';
import LeadGradeFilter from './filter/LeadGradeFilter';
import OwnerFilter from 'domain/sales/shared/filter/OwnerFilter';
import LeadSourceFilter from './filter/LeadSourceFilter';
import { useLocation, Location } from 'react-router-dom';

const DAYS_COLOR_BORDER = {
  greenBorder: 1.5,
  yellowBorder: 3.5,
  lightRedBorder: 7.5,
};

const EXCEED_DAYS = 21;

const leadsTableSx = {
  '& tbody .highlighted': {
    backgroundColor: 'background.success',
  },
  '& table.list-general': {
    minWidth: '1500px',
    '& .list-btn-container': {
      pl: '28px',
    },
    '& .th-buttons': {
      width: '120px',
    },
  },
  '& table td span': {
    whiteSpace: 'nowrap',
  },
};

const LeadsListContent = () => {
  const location: Location<{ anyFields: string }> = useLocation();
  const { quickFilter, filters: currentFilters } = useFilterContext();
  const { isGranted } = useAuthorization();
  const { getUserAlias } = useAuthenticatedUserService();
  const { getCreateUrl, getSingleEntityUrl } = useLeadUrl();
  const currentUserAlias = useAsyncValue(async () => getUserAlias());
  const shouldShowCLevelFilters = isGranted(READ_FULL_LEADS_LIST);

  const qualityLeadsFilterState = {
    status: { in: ['NEW', 'ASSIGNED', 'OPEN'] },
    grade: { in: ['A_PLUS', 'A', 'B_PLUS'] },
  };

  const myLeadsFilterState = {
    status: { in: ['NEW', 'ASSIGNED', 'OPEN'] },
    'owner.alias': {
      in: [currentUserAlias],
    },
  };

  const allLeadsState = {
    grade: { in: [NULL_AS_STRING, 'A_PLUS', 'A', 'B_PLUS', 'B', 'B_MINUS', 'C'] },
  };

  useDidUpdateEffect(() => {
    const urlFilters = getUrlStorageParams(location).filter;
    const stringifyCurrentFilters = currentFilters;

    if (
      urlFilters &&
      stringifyCurrentFilters &&
      JSON.stringify(stringifyCurrentFilters) !== JSON.stringify(urlFilters)
    ) {
      quickFilter(getUrlStorageParams(location).filter || {});
    }
  }, [location]);

  const filters = (
    <FiltersLayout
      quickFilters={[
        <QuickFilter label="My leads" state={myLeadsFilterState} />,
        ...(shouldShowCLevelFilters ? [<QuickFilter label="Quality leads" state={qualityLeadsFilterState} />] : []),
        ...(shouldShowCLevelFilters ? [<QuickFilter label="All leads" state={allLeadsState} />] : []),
      ]}
    >
      <LeadStatusFilter label="Lead status" source="status" />
      <LeadGradeFilter label="Lead grade" source="grade" />
      <OwnerFilter label="Lead owner" source="owner.alias" isVisible={isGranted(READ_FULL_LEADS_LIST)} />
      <LeadSourceFilter label="Lead source" source="source" />
      <DateTimeRangeFilter label="Created date" source="createdAt" />
    </FiltersLayout>
  );

  return (
    <ListPageLayout
      header={<ListHeader label="Leads" icon="fa-child" />}
      actions={[
        <CreateButton url={getCreateUrl()} label="Create lead" key="create-lead" permissionToCheck={CREATE_LEAD} />,
      ]}
      search={<LeadSearchInput label="name, phone, email, company" />}
      filter={filters}
    >
      <DataGridLayout
        rowProps={lead => (lead.highlighted ? { className: 'highlighted' } : {}) as any}
        buttons={[
          <EditLeadButton
            key="edit-lead"
            source="id"
            outline
            permissionToCheck={UPDATE_LEAD_FROM_LIST_PAGE}
            isVisible={lead => LEAD_STATUS[lead.status] !== LEAD_STATUS.QUALIFIED}
            sx={{ height: '2em' }}
          />,
        ]}
        sxTableWrapper={leadsTableSx}
        isSmallTable
      >
        <SearchLinkField source="id" url={getSingleEntityUrl} label="ID" isSortable width={50} />
        <ExpandingTooltipField
          value={lead => `${lead.firstName} ${lead.middleName || ''} ${lead.lastName}`}
          label="Name"
          source="lastName"
          isSortable
          maxValueLength={12}
          width={150}
        >
          <SearchResultField source="lastName" label="Last name" />
        </ExpandingTooltipField>
        <ExpandingTooltipField source="companyName" label="Company" isSortable maxValueLength={12} width={150}>
          <SearchResultField source="companyName" />
        </ExpandingTooltipField>
        <ExpandingTooltipField source="email" label="Email" isSortable maxValueLength={12} width={150}>
          <EmailSearchField source="email" />
        </ExpandingTooltipField>
        <CountryFlagField source="country" label="Country" width={80} hasTooltip />
        <BadgeField
          badges={[
            <LeadWillAutoDisqualifiedSoonBadge
              id="disqualifiedSoon"
              source="updatedAt"
              statusSource="status"
              skipStates={NOT_ACTIVE_STATUSES}
            />,
          ]}
          label="Last touch"
          source="updatedAt"
          isSortable
          width={140}
        >
          <LastTouchCrmField
            statusSource="status"
            colorBorders={DAYS_COLOR_BORDER}
            skipStates={NOT_ACTIVE_STATUSES}
            exceedDaysForStale={EXCEED_DAYS}
          />
        </BadgeField>
        <LeadStatusField source="status" label="Status" isSortable />
        <EmployeeLinkField source="ownerAlias" nameSource="ownerName" label="Owner" isSortable />
        <LeadGradeField source="grade" label="Grade" isSortable width={80} />
        <LeadAgeField
          source="leadAge.days"
          label="Age"
          skipStates={FINALLY_NOT_ACTIVE_STATUSES}
          isSortable
          width={100}
        />
        <ReadByOwnerCheckboxField
          source="isVisitedByOwner"
          statusSource="status"
          skipStates={FINALLY_NOT_ACTIVE_STATUSES}
          label="Unread"
          revertValue
          width={70}
        />
      </DataGridLayout>
    </ListPageLayout>
  );
};

export default LeadsListContent;
